'use client'
import Link from 'next/link';
import { useEffect, useState } from 'react';
import IconSprite from "../common/IconSprite";
import { useSearchParams } from 'next/navigation';

interface PaginationProps {
    currentPage?: number;
    lastPage?: number;
    basePath?: string;
    totalResults?: number;
    perPage?: number;
    result?: string | "elementos";
    hideResults?: boolean;
    scroll?: boolean;
    isUsedProduct?: boolean; 
}

export default function Pagination({
    result = "elementos",
    currentPage = 1,
    lastPage = 1,
    basePath = '',
    totalResults = 0,
    perPage = 10,
    hideResults = false,
    scroll = true,
    isUsedProduct = false,
}: PaginationProps) {
    const [isMobile, setIsMobile] = useState(false);
    const searchParams = useSearchParams();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    let pages: number[] = [];
    const maxPages = isMobile ? 2 : 5;

    if (lastPage <= maxPages) {
        pages = Array.from({ length: lastPage }, (_, i) => i + 1);
    } else {
        let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
        let endPage = Math.min(lastPage, startPage + maxPages - 1);

        if (currentPage <= Math.ceil(maxPages / 2)) {
            startPage = 1;
            endPage = maxPages;
        } else if (currentPage >= lastPage - Math.floor(maxPages / 2)) {
            startPage = lastPage - (maxPages - 1);
            endPage = lastPage;
        }

        pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    }

    const getHref = (page: number): string => {

        const params = new URLSearchParams(searchParams.toString());

        isUsedProduct ? params.set('pagina', page.toString()) : params.set('page', page.toString());

        return `${basePath}?${params.toString()}`;
    };
    
    const totalVisible = Math.max(0, Math.min(perPage, totalResults - (currentPage - 1) * perPage));

    const buttonBaseClass = "w-10 h-10 flex items-center justify-center transition";
    const activeButtonClass = `${buttonBaseClass} bg-white text-black font-bold text-xl`;
    const inactiveButtonClass = `${buttonBaseClass} bg-white hover:bg-gray-300 text-xl`;
    const primaryButtonClass = `${buttonBaseClass} bg-yellow-400 hover:bg-yellow-500`;
    const disabledButtonClass = `${buttonBaseClass} bg-gray02 cursor-not-allowed text-dark02`;

    const buttonBaseUsedProductClass = "w-10 h-10 flex items-center justify-center transition rounded-md border border-gray01 font-roboto text-[14px] font-bold";
    const activeButtonUsedProductClass = `${buttonBaseUsedProductClass} bg-white border-matco text-black`;
    const inactiveButtonUsedProductClass = `${buttonBaseUsedProductClass} bg-white hover:bg-gray-300`;
    const primaryButtonUsedProductClass = `${buttonBaseUsedProductClass} bg-white hover:bg-yellow-500`;
    const disabledButtonUsedProductClass = `${buttonBaseUsedProductClass} bg-gray02 cursor-not-allowed text-dark02`;

    return (
        <>
            {!hideResults ? (
                    <nav aria-label="Paginación" className="flex flex-col md:flex-row items-center justify-between py-2 bg-gray-100 rounded-md mt-8">
                        {!isMobile && (
                            <div className="text-dark02 text-lg font-light">
                                Mostrando {totalVisible} de {totalResults} {result}
                            </div>
                        )}

                        <ul className={`flex items-center space-x-2 ${isMobile ? 'justify-center w-full' : ''} h-full`}>
                            <li className="flex items-center h-full">
                                {currentPage > 1 ? (
                                    <Link href={getHref(1)} scroll={scroll}>
                                        <button className={primaryButtonClass}>
                                            <IconSprite id="ic_arrow_left_double" className="w-4 h-4" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonClass}>
                                        <IconSprite id="ic_arrow_left_double" className="w-4 h-4" />
                                    </button>
                                )}
                            </li>

                            <li>
                                {currentPage > 1 ? (
                                    <Link href={getHref(currentPage - 1)} scroll={scroll}>
                                        <button className={primaryButtonClass}>
                                            <IconSprite id="ic_arrow_left" className="w-4 h-4" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonClass}>
                                        <IconSprite id="ic_arrow_left" className="w-4 h-4" />
                                    </button>
                                )}
                            </li>

                            {pages.map((page) => (
                                <li key={page}>
                                    {page === currentPage ? (
                                        <span className={activeButtonClass}>
                                            {page.toString().padStart(2, '0')}
                                        </span>
                                    ) : (
                                        <Link href={getHref(page)} scroll={scroll}>
                                            <button className={inactiveButtonClass}>
                                                {page.toString().padStart(2, '0')}
                                            </button>
                                        </Link>
                                    )}
                                </li>
                            ))}

                            <li>
                                {currentPage < lastPage ? (
                                    <Link href={getHref(currentPage + 1)} scroll={scroll}>
                                        <button className={primaryButtonClass}>
                                            <IconSprite id="ic_arrow_right" className="w-4 h-4" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonClass}>
                                        <IconSprite id="ic_arrow_right" className="w-4 h-4" />
                                    </button>
                                )}
                            </li>

                            <li>
                                {currentPage < lastPage ? (
                                    <Link href={getHref(lastPage)} scroll={scroll}>
                                        <button className={primaryButtonClass}>
                                            <IconSprite id="ic_arrow_right_double" className="w-4 h-4" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonClass}>
                                        <IconSprite id="ic_arrow_right_double" className="w-4 h-4" />
                                    </button>
                                )}
                            </li>
                        </ul>
                    </nav>
                ) : (
                    <nav aria-label="Paginación" className="flex flex-col md:flex-row items-center justify-between py-2 bg-transparent rounded-md mt-8">

                        <ul className={`flex items-center space-x-2 justify-center w-full h-full`}>
                            <li className="flex items-center h-full">
                                {currentPage > 1 ? (
                                    <Link href={getHref(1)} scroll={scroll}>
                                        <button className={primaryButtonUsedProductClass}>
                                            <IconSprite id="ic_arrow_left_double_gray" className="w-3 h-3" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonUsedProductClass}>
                                        <IconSprite id="ic_arrow_left_double_gray" className="w-3 h-3" />
                                    </button>
                                )}
                            </li>

                            <li>
                                {currentPage > 1 ? (
                                    <Link href={getHref(currentPage - 1)} scroll={scroll}>
                                        <button className={primaryButtonUsedProductClass}>
                                            <IconSprite id="ic_arrow_left_gray" className="w-3 h-3" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonUsedProductClass}>
                                        <IconSprite id="ic_arrow_left_gray" className="w-3 h-3" />
                                    </button>
                                )}
                            </li>

                            {pages.map((page) => (
                                <li key={page}>
                                    {page === currentPage ? (
                                        <span className={activeButtonUsedProductClass}>
                                            {page.toString().padStart(2, '0')}
                                        </span>
                                    ) : (
                                        <Link href={getHref(page)} scroll={scroll}>
                                            <button className={inactiveButtonUsedProductClass}>
                                                {page.toString().padStart(2, '0')}
                                            </button>
                                        </Link>
                                    )}
                                </li>
                            ))}

                            <li>
                                {currentPage < lastPage ? (
                                    <Link href={getHref(currentPage + 1)} scroll={scroll}>
                                        <button className={primaryButtonUsedProductClass}>
                                            <IconSprite id="ic_arrow_right_gray" className="w-3 h-3" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonUsedProductClass}>
                                        <IconSprite id="ic_arrow_right_gray" className="w-3 h-3" />
                                    </button>
                                )}
                            </li>

                            <li>
                                {currentPage < lastPage ? (
                                    <Link href={getHref(lastPage)} scroll={scroll}>
                                        <button className={primaryButtonUsedProductClass}>
                                            <IconSprite id="ic_arrow_right_double_gray" className="w-3 h-3" />
                                        </button>
                                    </Link>
                                ) : (
                                    <button disabled className={disabledButtonUsedProductClass}>
                                        <IconSprite id="ic_arrow_right_double_gray" className="w-3 h-3" />
                                    </button>
                                )}
                            </li>
                        </ul>
                    </nav>
                )
            }
        </>
    );
}
